// src/pages/Buyer/Payment.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
} from '@mui/material';
import {
  AttachMoney,
  Upload,
  CheckCircle,
  Receipt,
  ArrowBack,
  Download,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { paymentAPI, enquiryAPI } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

const BuyerPayment = () => {
  const { enquiryId } = useParams();
  const navigate = useNavigate();
  
  const [paymentData, setPaymentData] = useState(null);
  const [enquiryData, setEnquiryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [paymentType, setPaymentType] = useState('advance');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [transactionId, setTransactionId] = useState('');
  const [paymentProof, setPaymentProof] = useState(null);

  useEffect(() => {
    fetchPaymentData();
    fetchEnquiryData();
  }, [enquiryId]);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const response = await paymentAPI.getPayments(enquiryId);
      setPaymentData(response.data);
      
      // Set active step based on payment status
      if (response.data.balance_paid) {
        setActiveStep(2);
      } else if (response.data.advance_paid) {
        setActiveStep(1);
        setPaymentType('balance');
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
      toast.error('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnquiryData = async () => {
    try {
      const response = await enquiryAPI.getById(enquiryId);
      setEnquiryData(response.data);
    } catch (error) {
      console.error('Error fetching enquiry data:', error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPaymentProof(file);
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentProof) {
      toast.error('Please upload payment proof');
      return;
    }

    if (!transactionId.trim()) {
      toast.error('Please enter transaction ID/reference');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('enquiry_id', enquiryId);
      formData.append('payment_type', paymentType);
      formData.append('payment_method', paymentMethod);
      formData.append('transaction_id', transactionId);
      formData.append('payment_proof', paymentProof);

      await paymentAPI.submit(formData);
      
      toast.success(`${paymentType === 'advance' ? 'Advance' : 'Balance'} payment submitted successfully!`);
      fetchPaymentData(); // Refresh data
      
      if (paymentType === 'advance') {
        setActiveStep(1);
        setPaymentType('balance');
      } else {
        setActiveStep(2);
      }
      
      // Clear form
      setPaymentProof(null);
      setTransactionId('');
    } catch (error) {
      console.error('Error submitting payment:', error);
      toast.error(error.response?.data?.error || 'Payment submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ['Advance Payment (30%)', 'Balance Payment (70%)', 'Payment Complete'];

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading payment information...</Typography>
      </Container>
    );
  }

  const totalAmount = paymentData?.total_amount || 0;
  const advanceAmount = totalAmount * 0.3;
  const balanceAmount = totalAmount * 0.7;

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
      case 1:
        return (
          <PaymentFormStep
            paymentType={paymentType}
            totalAmount={totalAmount}
            advanceAmount={advanceAmount}
            balanceAmount={balanceAmount}
            paymentMethod={paymentMethod}
            transactionId={transactionId}
            paymentProof={paymentProof}
            onPaymentMethodChange={setPaymentMethod}
            onTransactionIdChange={setTransactionId}
            onFileUpload={handleFileUpload}
            onSubmit={handleSubmitPayment}
            submitting={submitting}
            existingPayments={paymentData?.payments || []}
          />
        );
      case 2:
        return <PaymentCompleteStep />;
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/buyer/enquiries')}
          sx={{ mb: 2 }}
        >
          Back to Enquiries
        </Button>
        
        <Typography variant="h4" gutterBottom>
          Payment - Enquiry #{enquiryId}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete payment for your fruit order
        </Typography>
      </Box>

      {/* Order Summary */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Order Summary
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 'bold' }}>Enquiry ID</TableCell>
                <TableCell>#{enquiryId}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 'bold' }}>Total Amount</TableCell>
                <TableCell>{formatCurrency(totalAmount)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 'bold' }}>Advance Required (30%)</TableCell>
                <TableCell>{formatCurrency(advanceAmount)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 'bold' }}>Balance Amount (70%)</TableCell>
                <TableCell>{formatCurrency(balanceAmount)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: 'bold' }}>Payment Status</TableCell>
                <TableCell>
                  {paymentData?.advance_paid ? (
                    <Chip label="Advance Paid" color="success" size="small" />
                  ) : (
                    <Chip label="Advance Pending" color="warning" size="small" />
                  )}
                  {paymentData?.balance_paid && (
                    <Chip label="Balance Paid" color="success" size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Payment Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Payment Form/Status */}
      <Paper sx={{ p: 3, mb: 4 }}>
        {renderStepContent(activeStep)}
      </Paper>

      {/* Payment History */}
      {paymentData?.payments && paymentData.payments.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Payment History
          </Typography>
          <TableContainer>
            <Table>
              <TableBody>
                {paymentData.payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Receipt />
                        <Typography variant="body2" fontWeight="medium">
                          {payment.payment_type === 'advance' ? 'Advance Payment' : 'Balance Payment'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatCurrency(payment.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status}
                        color={payment.status === 'verified' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(payment.created_at)}</TableCell>
                    <TableCell>
                      {payment.payment_proof_path && (
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => window.open(`http://localhost:5000${payment.payment_proof_path}`, '_blank')}
                        >
                          Receipt
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

const PaymentFormStep = ({
  paymentType,
  totalAmount,
  advanceAmount,
  balanceAmount,
  paymentMethod,
  transactionId,
  paymentProof,
  onPaymentMethodChange,
  onTransactionIdChange,
  onFileUpload,
  onSubmit,
  submitting,
  existingPayments,
}) => {
  const amount = paymentType === 'advance' ? advanceAmount : balanceAmount;
  const isAdvancePaid = existingPayments.some(p => p.payment_type === 'advance' && p.status === 'verified');

  return (
    <Box>
      <Alert 
        severity={paymentType === 'advance' ? 'info' : 'warning'} 
        sx={{ mb: 3 }}
      >
        <Typography variant="body1" fontWeight="medium">
          {paymentType === 'advance' 
            ? 'Advance Payment Required'
            : 'Balance Payment Due'}
        </Typography>
        <Typography variant="body2">
          {paymentType === 'advance'
            ? 'Pay 30% advance to confirm your order. Shipment will begin after payment verification.'
            : 'Pay 70% balance before shipment dispatch. Ensure payment is completed for timely delivery.'}
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ mb: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney /> Payment Details
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>Total Order Value</TableCell>
                      <TableCell align="right">{formatCurrency(totalAmount)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ fontWeight: 'bold' }}>
                        {paymentType === 'advance' ? 'Advance (30%)' : 'Balance (70%)'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'primary.main' }}>
                        {formatCurrency(amount)}
                      </TableCell>
                    </TableRow>
                    {paymentType === 'balance' && (
                      <TableRow>
                        <TableCell component="th" sx={{ fontWeight: 'bold' }}>Advance Paid</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={isAdvancePaid ? 'Paid ✓' : 'Pending'} 
                            color={isAdvancePaid ? 'success' : 'warning'} 
                            size="small" 
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  • Bank details will be provided after enquiry confirmation
                  <br />
                  • Payments are verified within 24 hours
                  <br />
                  • Keep your transaction ID for reference
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Upload Payment Proof
            </Typography>
            <input
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: 'none' }}
              id="payment-proof-upload"
              type="file"
              onChange={onFileUpload}
            />
            <label htmlFor="payment-proof-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<Upload />}
                fullWidth
              >
                Upload Bank Receipt/Screenshot
              </Button>
            </label>
            {paymentProof && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Selected: {paymentProof.name}
              </Typography>
            )}
          </Box>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={paymentMethod}
              label="Payment Method"
              onChange={(e) => onPaymentMethodChange(e.target.value)}
            >
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="upi">UPI</MenuItem>
              <MenuItem value="international_transfer">International Wire Transfer</MenuItem>
              <MenuItem value="credit_card">Credit Card</MenuItem>
              <MenuItem value="debit_card">Debit Card</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Transaction ID/Reference"
            value={transactionId}
            onChange={(e) => onTransactionIdChange(e.target.value)}
            placeholder="Enter bank transaction reference number"
            sx={{ mb: 3 }}
          />
          
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={onSubmit}
            disabled={!paymentProof || !transactionId.trim() || submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <AttachMoney />}
          >
            {submitting ? 'Processing...' : `Submit ${paymentType === 'advance' ? 'Advance' : 'Balance'} Payment`}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

const PaymentCompleteStep = () => {
  return (
    <Box textAlign="center" py={4}>
      <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Payment Completed Successfully!
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
        Your payment has been submitted and is being verified. 
        You will receive a confirmation email once verification is complete.
      </Typography>
      
      <Alert severity="success" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
        <Typography variant="body1" fontWeight="medium">
          Next Steps:
        </Typography>
        <Typography variant="body2">
          • Track payment verification status in your dashboard
          <br />
          • Our team will contact you for shipping arrangements
          <br />
          • You can track shipment progress once dispatched
        </Typography>
      </Alert>
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => window.location.href = '/buyer/dashboard'}
          sx={{ minWidth: 200 }}
        >
          Go to Dashboard
        </Button>
        
        <Button
          variant="outlined"
          size="large"
          onClick={() => window.location.href = '/buyer/enquiries'}
          sx={{ minWidth: 200 }}
        >
          View Enquiries
        </Button>
      </Box>
    </Box>
  );
};

export default BuyerPayment;